/**
 * API istemcisi.
 *
 * Sunucu sözleşmesi (docs/PLAN.md):
 *   - Hata gövdesi daima { error: { code, message, details, request_id } }
 *   - `code` makine okunur ve sabit; istemci ona göre dallanır
 *   - `message` zaten kullanıcının dilinde gelir — istemci onu tekrar çevirmez
 *   - Erişim jetonu 15 dk, yenileme jetonu rotasyonlu
 */

import { useAuthStore } from '@/stores/auth'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** Sunucudan gelen hata. `code` üzerinden dallanılır, `message` gösterilir. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Alan bazlı doğrulama hatalarını { alan: mesaj } biçimine indirger. */
  get fieldErrors(): Record<string, string> {
    const fields = this.details?.fields as
      | Array<{ field: string; rule: string; message?: string }>
      | undefined
    if (!fields) return {}

    const out: Record<string, string> = {}
    for (const f of fields) {
      // Aynı alanın birden çok kuralı varsa ilkini göster; kullanıcıya
      // beş kural birden dökmek yardımcı olmuyor.
      if (!out[f.field]) out[f.field] = f.message ?? f.rule
    }
    return out
  }
}

/** Ağ katmanı hatası — sunucuya hiç ulaşılamadı. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('network_unreachable')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Jeton eklemeden gönder (giriş, kayıt, yenileme). */
  anonymous?: boolean
  /** Süresi dolan jetonu yenileyip tekrar deneme (özyinelemeyi keser). */
  skipRefresh?: boolean
  /** Mutasyonların tekrar gönderiminde çift kayıt olmasın diye. */
  idempotencyKey?: string
}

/**
 * Eşzamanlı yenileme koruması.
 *
 * Beş istek aynı anda 401 alırsa beş yenileme isteği gitmemeli: ilki
 * rotasyonu yapar, diğer dördü iptal edilmiş jetonu kullanır ve sunucu
 * bunu "jeton çalındı" sayıp tüm oturumu düşürür. Bekleyen yenileme
 * varsa herkes onu bekler.
 */
let refreshInFlight: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    const auth = useAuthStore()
    refreshInFlight = auth.refresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const auth = useAuthStore()
  const { body, anonymous, skipRefresh, idempotencyKey, headers, ...rest } = options

  const requestHeaders = new Headers(headers)
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Accept-Language', auth.locale)

  if (body !== undefined) {
    requestHeaders.set('Content-Type', 'application/json')
  }
  if (!anonymous && auth.accessToken) {
    requestHeaders.set('Authorization', `Bearer ${auth.accessToken}`)
  }
  if (!anonymous && auth.activeHouseholdId) {
    requestHeaders.set('X-Household-Id', auth.activeHouseholdId)
  }
  if (idempotencyKey) {
    requestHeaders.set('Idempotency-Key', idempotencyKey)
  }

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: requestHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (cause) {
    throw new NetworkError(cause)
  }

  // Süresi dolan jetonu bir kez yenileyip isteği tekrarla. Kullanıcı
  // 15 dakikada bir giriş ekranına atılmamalı.
  if (response.status === 401 && !anonymous && !skipRefresh) {
    const code = await peekErrorCode(response)
    if (code === 'token_expired' && (await refreshOnce())) {
      return request<T>(path, { ...options, skipRefresh: true })
    }
    auth.clearSession()
  }

  if (response.status === 204) {
    return undefined as T
  }

  const payload = await parseJson(response)

  if (!response.ok) {
    const error = (payload as { error?: Record<string, unknown> })?.error
    throw new ApiError(
      (error?.code as string) ?? 'internal_error',
      (error?.message as string) ?? 'Request failed',
      response.status,
      error?.details as Record<string, unknown> | undefined,
      error?.request_id as string | undefined,
    )
  }

  return payload as T
}

/** Gövdeyi tüketmeden hata kodunu okur. */
async function peekErrorCode(response: Response): Promise<string | undefined> {
  try {
    const clone = response.clone()
    const payload = await clone.json()
    return payload?.error?.code
  } catch {
    return undefined
  }
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return undefined
  try {
    return JSON.parse(text)
  } catch {
    // Sunucu JSON dönmediyse (vekil hatası, HTML hata sayfası) ham metni
    // yutmak yerine anlaşılır bir hataya çeviriyoruz.
    throw new ApiError('internal_error', 'Sunucudan beklenmeyen cevap', response.status)
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  // PUT, PATCH'ten farklı olarak kaynağın TAMAMINI değiştirir. Bütçe
  // satırlarında doğru fiil bu: gönderilen liste eskisinin yerine geçiyor,
  // eksik bırakılan satır silinmiş sayılıyor.
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
